/**
 * SnowflakeConfigForm — Snowflake warehouse connection configuration form.
 *
 * Supports three authentication modes with grouped fields:
 * - Managed Key (default): auto-generated key pair with setup SQL
 * - Password: traditional username/password
 * - Key Pair: user-provided RSA key pair
 *
 * SQL generation is fully client-side (pure function, no server round-trip).
 * Keypair is auto-fetched on mount when authType is managed_key.
 */
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, CircleCheckBig, CircleX, ClipboardCopy, Download, Key, LoaderCircle, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { oneLight, SyntaxHighlighter } from '@/_shared/lib/syntax';

import { generateSetupSQL } from '../lib/snowflake-setup';

export type AuthType = 'password' | 'key_pair' | 'managed_key';

export interface SnowflakeConnectionConfig {
  account: string;
  username: string;
  database: string;
  warehouse: string;
  schema?: string;
  role?: string;
  authType: AuthType;
  password?: string;
  privateKey?: string;
  privateKeyPassphrase?: string;
}

export interface TestResult {
  success: boolean;
  database?: string;
  tableCount?: number;
  error?: string;
}

interface SnowflakeConfigFormProps {
  rudderDestinationId: string;
  initialConfig?: SnowflakeConnectionConfig;
  isAdmin: boolean;
  onSave: (rudderDestinationId: string, config: SnowflakeConnectionConfig) => void;
  onTest: (rudderDestinationId: string, config: SnowflakeConnectionConfig) => void;
  onRemove: (rudderDestinationId: string) => void;
  isConfigured: boolean;
  isSaving: boolean;
  isTesting: boolean;
  testResult?: TestResult;
}

const REDACTED = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';

const inputClass =
  'w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-brand';

const labelClass = 'mb-1 block text-xs text-muted-foreground';

/** Generate a default Snowflake username from a workspace ID/slug. */
function defaultManagedUsername(workspaceId: string): string {
  return `RUDDER_AI_${workspaceId.replace(/-/g, '_').toUpperCase()}`;
}

function CopyButton({ text, label }: { text: string; label?: string }): React.ReactElement {
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(text);
    toast.success(label ? `${label} copied` : 'Copied');
  }, [text, label]);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void handleCopy();
      }}
      className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[0.6875rem] text-muted-foreground transition-colors hover:border-brand hover:text-brand"
    >
      <ClipboardCopy size={11} />
      Copy
    </button>
  );
}

function DownloadButton({ text, filename }: { text: string; filename: string }): React.ReactElement {
  const handleDownload = useCallback(() => {
    const blob = new Blob([text], { type: 'application/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [text, filename]);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleDownload();
      }}
      className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[0.6875rem] text-muted-foreground transition-colors hover:border-brand hover:text-brand"
    >
      <Download size={11} />
      .sql
    </button>
  );
}

export function SnowflakeConfigForm({
  rudderDestinationId,
  initialConfig,
  isAdmin,
  onSave,
  onTest,
  onRemove,
  isConfigured,
  isSaving,
  isTesting,
  testResult,
}: SnowflakeConfigFormProps): React.ReactElement {
  const { slug: workspaceSlug } = useWorkspace();
  const orpc = useOrpc();

  const [authType, setAuthType] = useState<AuthType>(initialConfig?.authType ?? 'managed_key');
  const [account, setAccount] = useState(initialConfig?.account ?? '');
  const [username, setUsername] = useState(initialConfig?.username ?? '');
  const [password, setPassword] = useState(initialConfig?.password ?? '');
  const [privateKey, setPrivateKey] = useState(initialConfig?.privateKey ?? '');
  const [passphrase, setPassphrase] = useState(initialConfig?.privateKeyPassphrase ?? '');
  const [database, setDatabase] = useState(initialConfig?.database ?? '');
  const [warehouse, setWarehouse] = useState(initialConfig?.warehouse ?? '');
  const [schema, setSchema] = useState(initialConfig?.schema ?? '');
  const [role, setRole] = useState(initialConfig?.role ?? '');

  // Managed Key state
  const [readWrite, setReadWrite] = useState(false);
  const [setupSQL, setSetupSQL] = useState<string | null>(null);
  const [keypairInfo, setKeypairInfo] = useState<{
    publicKeyPem: string;
    publicKeyBody: string;
    fingerprint: string;
    egressIPs: string | null;
    networkPolicyName: string | null;
  } | null>(null);
  const [sqlExpanded, setSqlExpanded] = useState(true);

  // Managed username — use entered value if provided, otherwise derive from workspace slug
  const managedUsername = username.trim() || defaultManagedUsername(workspaceSlug);

  // Role placeholder — {username}_ROLE
  const rolePlaceholder = useMemo(() => {
    const base = managedUsername || defaultManagedUsername(workspaceSlug);
    return `${base.toUpperCase()}_ROLE`;
  }, [managedUsername, workspaceSlug]);

  // Clear stale setup SQL when relevant fields change so users must regenerate
  useEffect(() => {
    setSetupSQL(null);
  }, [username, database, warehouse, schema, role, readWrite]);

  const keypairMutation = useMutation(
    orpc.settings.warehouses.getOrCreateKeypair.mutationOptions({
      onSuccess: (data) => {
        setKeypairInfo({
          publicKeyPem: data.publicKeyPem,
          publicKeyBody: data.publicKeyBody,
          fingerprint: data.fingerprint,
          egressIPs: data.egressIPs ?? null,
          networkPolicyName: data.networkPolicyName ?? null,
        });
      },
      onError: () => {
        didAutoFetch.current = false;
        toast.error('Failed to generate key pair');
      },
    }),
  );
  const keypairMutate = keypairMutation.mutate;

  // Auto-fetch keypair on mount when authType is managed_key
  const didAutoFetch = useRef(false);
  // When true, SQL generation is pending on keypair becoming available
  const pendingSQLGeneration = useRef(false);
  useEffect(() => {
    if (authType === 'managed_key' && !didAutoFetch.current) {
      didAutoFetch.current = true;
      keypairMutate({});
    }
  }, [authType, keypairMutate]);

  const doGenerateSQL = useCallback(
    (kp: NonNullable<typeof keypairInfo>) => {
      try {
        const sql = generateSetupSQL({
          username: managedUsername,
          publicKeyBody: kp.publicKeyBody,
          warehouse: warehouse.trim(),
          database: database.trim(),
          schema: schema.trim() || undefined,
          role: role.trim() || undefined,
          readWrite,
          egressIPs: kp.egressIPs ?? undefined,
          networkPolicyName: kp.networkPolicyName ?? undefined,
        });
        setSetupSQL(sql);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Invalid identifier');
      }
    },
    [managedUsername, warehouse, database, schema, role, readWrite],
  );

  // When keypairInfo arrives and SQL generation is pending, auto-generate
  useEffect(() => {
    if (keypairInfo && pendingSQLGeneration.current) {
      pendingSQLGeneration.current = false;
      doGenerateSQL(keypairInfo);
    }
  }, [keypairInfo, doGenerateSQL]);

  const handleGenerateSQL = useCallback(() => {
    if (!keypairInfo) {
      // Fetch keypair first — SQL generation will fire from the effect above
      pendingSQLGeneration.current = true;
      keypairMutation.mutate({});
      return;
    }

    doGenerateSQL(keypairInfo);
  }, [keypairInfo, keypairMutation, doGenerateSQL]);

  const buildConfig = useCallback((): SnowflakeConnectionConfig => {
    const config: SnowflakeConnectionConfig = {
      account: account.trim(),
      username: authType === 'managed_key' ? managedUsername : username.trim(),
      database: database.trim(),
      warehouse: warehouse.trim(),
      authType,
    };
    if (schema.trim()) config.schema = schema.trim();
    if (role.trim()) config.role = role.trim();

    if (authType === 'password') {
      if (password && !password.includes('\u2022\u2022\u2022\u2022')) {
        config.password = password;
      }
    } else if (authType === 'key_pair') {
      if (privateKey && !privateKey.includes('\u2022\u2022\u2022\u2022')) {
        config.privateKey = privateKey;
      }
      if (passphrase && !passphrase.includes('\u2022\u2022\u2022\u2022')) {
        config.privateKeyPassphrase = passphrase;
      }
    }
    // managed_key: no secrets from frontend — backend injects them
    return config;
  }, [
    account,
    username,
    managedUsername,
    database,
    warehouse,
    schema,
    role,
    authType,
    password,
    privateKey,
    passphrase,
  ]);

  const hasRedacted =
    authType === 'password'
      ? password.includes('\u2022\u2022\u2022\u2022')
      : authType === 'key_pair'
        ? privateKey.includes('\u2022\u2022\u2022\u2022') || passphrase.includes('\u2022\u2022\u2022\u2022')
        : false;

  const requiredFilled =
    account.trim() !== '' &&
    (authType === 'managed_key' ? managedUsername !== '' : username.trim() !== '') &&
    database.trim() !== '' &&
    warehouse.trim() !== '';

  const saveDisabled =
    !isAdmin ||
    isSaving ||
    !requiredFilled ||
    hasRedacted ||
    (authType === 'managed_key' && !keypairInfo && !isConfigured);

  const handleSave = useCallback(() => {
    onSave(rudderDestinationId, buildConfig());
  }, [onSave, rudderDestinationId, buildConfig]);

  const handleTest = useCallback(() => {
    onTest(rudderDestinationId, buildConfig());
  }, [onTest, rudderDestinationId, buildConfig]);

  const handleRemove = useCallback(() => {
    onRemove(rudderDestinationId);
  }, [onRemove, rudderDestinationId]);

  const authTabClass = (tab: AuthType) =>
    `px-3 py-1.5 text-xs font-medium transition-colors ${
      authType === tab ? 'bg-brand/10 text-brand' : 'text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="space-y-4">
      {/* Auth type toggle */}
      <div>
        <span className={labelClass}>Authentication Method</span>
        <div className="mt-1 inline-flex rounded border border-border" role="tablist">
          <button
            type="button"
            id="tab-managed-key"
            role="tab"
            aria-selected={authType === 'managed_key'}
            onClick={() => {
              setAuthType('managed_key');
              setUsername('');
              setPassword('');
              setPrivateKey('');
              setPassphrase('');
            }}
            className={authTabClass('managed_key')}
          >
            Managed Key
          </button>
          <button
            type="button"
            id="tab-password"
            role="tab"
            aria-selected={authType === 'password'}
            onClick={() => {
              setAuthType('password');
              setPrivateKey('');
              setPassphrase('');
            }}
            className={`border-l border-border ${authTabClass('password')}`}
          >
            Password
          </button>
          <button
            type="button"
            id="tab-key-pair"
            role="tab"
            aria-selected={authType === 'key_pair'}
            onClick={() => {
              setAuthType('key_pair');
              setPassword('');
            }}
            className={`border-l border-border ${authTabClass('key_pair')}`}
          >
            Key Pair
          </button>
        </div>
      </div>

      {/* Connection Settings */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">Connection</legend>
        <div>
          <label htmlFor="sf-account" className={labelClass}>
            Account <span className="text-red-500">*</span>
          </label>
          <input
            id="sf-account"
            type="text"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            placeholder="xy12345.us-east-1"
            className={inputClass}
          />
        </div>
      </fieldset>

      {/* Authentication */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">Authentication</legend>

        {authType === 'managed_key' ? (
          <>
            <div>
              <label htmlFor="sf-username" className={labelClass}>
                Service Username
              </label>
              <input
                id="sf-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={defaultManagedUsername(workspaceSlug)}
                className={inputClass}
              />
              <p className="mt-1 text-[0.6875rem] text-muted-foreground/70">
                Snowflake service user. Defaults to RUDDER_AI_ + workspace slug.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label htmlFor="sf-rw-toggle" className="text-xs text-muted-foreground">
                Access Level
              </label>
              <button
                id="sf-rw-toggle"
                type="button"
                role="switch"
                aria-checked={readWrite}
                onClick={() => setReadWrite(!readWrite)}
                className={`inline-flex items-center gap-2 rounded border px-3 py-1.5 text-xs font-medium transition-colors ${
                  readWrite
                    ? 'border-amber-400/50 bg-amber-50 text-amber-700'
                    : 'border-border bg-background text-muted-foreground'
                }`}
              >
                {readWrite ? 'Read & Write' : 'Read Only'}
              </button>
              <span className="text-[0.6875rem] text-muted-foreground/70">
                {readWrite ? 'Required for dbt model execution' : 'For dashboards and analytics queries'}
              </span>
            </div>

            {/* Public key info (auto-fetched on mount) */}
            {keypairInfo && (
              <div className="space-y-3 rounded border border-border bg-muted/30 px-3 py-3">
                {keypairInfo.publicKeyPem && (
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Public Key</span>
                      <CopyButton text={keypairInfo.publicKeyPem} label="Public Key" />
                    </div>
                    <pre className="max-h-32 overflow-auto rounded bg-background p-2 font-mono text-[0.625rem] text-foreground/80">
                      {keypairInfo.publicKeyPem}
                    </pre>
                  </div>
                )}
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Fingerprint</span>
                    <CopyButton text={keypairInfo.fingerprint} label="Fingerprint" />
                  </div>
                  <code className="block text-[0.6875rem] break-all text-foreground/80">{keypairInfo.fingerprint}</code>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <label htmlFor="sf-username" className={labelClass}>
                Username <span className="text-red-500">*</span>
              </label>
              <input
                id="sf-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ANALYST"
                className={inputClass}
              />
            </div>

            {authType === 'password' ? (
              <div>
                <label htmlFor="sf-password" className={labelClass}>
                  Password
                </label>
                <input
                  id="sf-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => {
                    if (password === REDACTED) setPassword('');
                  }}
                  placeholder="Enter password"
                  className={inputClass}
                />
              </div>
            ) : (
              <>
                <div>
                  <label htmlFor="sf-private-key" className={labelClass}>
                    Private Key
                  </label>
                  <textarea
                    id="sf-private-key"
                    value={privateKey}
                    onChange={(e) => setPrivateKey(e.target.value)}
                    onFocus={() => {
                      if (privateKey === REDACTED) setPrivateKey('');
                    }}
                    placeholder="-----BEGIN PRIVATE KEY-----"
                    rows={4}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="sf-passphrase" className={labelClass}>
                    Passphrase (optional)
                  </label>
                  <input
                    id="sf-passphrase"
                    type="password"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    onFocus={() => {
                      if (passphrase === REDACTED) setPassphrase('');
                    }}
                    placeholder="Private key passphrase"
                    className={inputClass}
                  />
                </div>
              </>
            )}
          </>
        )}
      </fieldset>

      {/* Warehouse Config */}
      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
          Warehouse Configuration
        </legend>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="sf-database" className={labelClass}>
              Database <span className="text-red-500">*</span>
            </label>
            <input
              id="sf-database"
              type="text"
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
              placeholder="ANALYTICS"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="sf-warehouse" className={labelClass}>
              Warehouse <span className="text-red-500">*</span>
            </label>
            <input
              id="sf-warehouse"
              type="text"
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              placeholder="COMPUTE_WH"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="sf-schema" className={labelClass}>
              Schema (optional)
            </label>
            <input
              id="sf-schema"
              type="text"
              value={schema}
              onChange={(e) => setSchema(e.target.value)}
              placeholder="PUBLIC"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="sf-role" className={labelClass}>
              Role (optional)
            </label>
            <input
              id="sf-role"
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder={authType === 'managed_key' ? rolePlaceholder : 'ANALYST_ROLE'}
              className={inputClass}
            />
          </div>
        </div>
      </fieldset>

      {/* Setup SQL — at the bottom, after all form fields (managed_key only) */}
      {authType === 'managed_key' && (
        <div className="space-y-3">
          <div>
            <button
              type="button"
              onClick={handleGenerateSQL}
              disabled={!warehouse.trim() || !database.trim()}
              className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
            >
              <Key size={12} />
              Generate Setup SQL
            </button>
            {(!warehouse.trim() || !database.trim()) && (
              <p className="mt-1 text-[0.6875rem] text-muted-foreground/70">
                Fill in Database and Warehouse above first.
              </p>
            )}
          </div>

          {/* Setup SQL output with syntax highlighting */}
          {setupSQL && (
            <div className="rounded border border-border bg-muted/30">
              <button
                type="button"
                onClick={() => setSqlExpanded(!sqlExpanded)}
                className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <span>Setup SQL</span>
                <div className="flex items-center gap-2">
                  <DownloadButton text={setupSQL} filename="rudder-ai-setup.sql" />
                  <CopyButton text={setupSQL} label="SQL" />
                  <ChevronDown size={14} className={`transition-transform ${sqlExpanded ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {sqlExpanded && (
                <div className="border-t border-border">
                  <p className="px-3 pt-2 text-[0.6875rem] text-muted-foreground">
                    Requires <strong>ACCOUNTADMIN</strong> privileges. Run in a Snowflake worksheet.
                  </p>
                  <div className="max-h-96 overflow-auto" data-testid="setup-sql-block">
                    <SyntaxHighlighter
                      language="sql"
                      style={oneLight}
                      customStyle={{
                        margin: 0,
                        padding: '0.5rem 0.75rem',
                        background: 'transparent',
                        fontSize: '0.6875rem',
                        lineHeight: '1.5',
                      }}
                    >
                      {setupSQL}
                    </SyntaxHighlighter>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleTest}
          disabled={!isAdmin || isTesting}
          className="inline-flex items-center gap-2 rounded border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand disabled:opacity-30"
        >
          {isTesting && <LoaderCircle size={12} className="animate-spin" />}
          Test
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saveDisabled}
          className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
        >
          {isSaving && <LoaderCircle size={12} className="animate-spin" />}
          Save
        </button>
        {authType === 'managed_key' && !keypairInfo && !isConfigured && (
          <span className="text-[0.6875rem] text-muted-foreground/70">Generate Setup SQL above before saving.</span>
        )}
        {isConfigured && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={!isAdmin || isSaving}
            className="ml-auto inline-flex items-center gap-1.5 rounded border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-700 disabled:opacity-30"
          >
            <Trash2 size={11} />
            Remove
          </button>
        )}
      </div>

      {/* Test result */}
      {testResult && (
        <div className={`flex items-center gap-2 text-xs ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
          {testResult.success ? (
            <>
              <CircleCheckBig size={14} />
              Connected -- {testResult.database} ({testResult.tableCount} tables)
            </>
          ) : (
            <>
              <CircleX size={14} />
              {testResult.error}
            </>
          )}
        </div>
      )}
    </div>
  );
}
